import { ActionIcon, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCheck, IconDotsVertical, IconEye, IconX } from "@tabler/icons-react";
import AcceptInterestModal from "@/pages/interests/components/AcceptInterestModal";
import InterestActionConfirmModal from "@/pages/interests/components/InterestActionConfirmModal";
import { CollaborationStatus } from "@/components/shared/constants/general.const";
import type { InterestRequestActionsProps } from "@/pages/interests/components/InterestRequestActions";

type SuggestedRowProps = {
  rowKind: "suggested";
  onViewProfile: () => void;
  onAcceptSuggestion?: () => void;
  onRejectSuggestion?: () => void;
  isLoading?: boolean;
};

type RequestRowProps = InterestRequestActionsProps & {
  rowKind: "request";
};

type InterestRequestTableActionsMenuProps = SuggestedRowProps | RequestRowProps;

export default function InterestRequestTableActionsMenu(props: InterestRequestTableActionsMenuProps) {
  const [acceptModalOpened, { open: openAcceptModal, close: closeAcceptModal }] = useDisclosure(false);
  const [rejectOpened, { open: openReject, close: closeReject }] = useDisclosure(false);
  const [suggestAcceptOpened, { open: openSuggestAccept, close: closeSuggestAccept }] = useDisclosure(false);
  const [suggestRejectOpened, { open: openSuggestReject, close: closeSuggestReject }] = useDisclosure(false);

  if (props.rowKind === "suggested") {
    const { onViewProfile, onAcceptSuggestion, onRejectSuggestion, isLoading = false } = props;

    return (
      <>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="md"
              radius="md"
              aria-label="Row actions"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <IconDotsVertical size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
            <Menu.Item
              leftSection={<IconEye size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                onViewProfile();
              }}
            >
              View
            </Menu.Item>
            {onAcceptSuggestion ? (
              <Menu.Item
                leftSection={<IconCheck size={16} />}
                onClick={(e) => {
                  e.stopPropagation();
                  openSuggestAccept();
                }}
              >
                Accept
              </Menu.Item>
            ) : null}
            {onRejectSuggestion ? (
              <Menu.Item
                leftSection={<IconX size={16} color="var(--mantine-color-red-6)" />}
                color="red"
                onClick={(e) => {
                  e.stopPropagation();
                  openSuggestReject();
                }}
              >
                Decline
              </Menu.Item>
            ) : null}
          </Menu.Dropdown>
        </Menu>

        {onAcceptSuggestion ? (
          <InterestActionConfirmModal
            opened={suggestAcceptOpened}
            onClose={closeSuggestAccept}
            title="Accept partner suggestion?"
            message="Do you want to accept this partner suggestion?"
            confirmLabel="Yes, accept"
            confirmColor="teal"
            isLoading={isLoading}
            onConfirm={() => {
              closeSuggestAccept();
              onAcceptSuggestion();
            }}
          />
        ) : null}

        {onRejectSuggestion ? (
          <InterestActionConfirmModal
            opened={suggestRejectOpened}
            onClose={closeSuggestReject}
            title="Reject partner suggestion?"
            message="Do you want to reject this partner suggestion?"
            confirmLabel="Yes, reject"
            confirmColor="red"
            isLoading={isLoading}
            onConfirm={() => {
              closeSuggestReject();
              onRejectSuggestion();
            }}
          />
        ) : null}
      </>
    );
  }

  const {
    matchId,
    companyId,
    collaborationStatus,
    isRequester,
    onAccept,
    onDecline,
    onViewProfile,
    onOpenWorkspace,
    isLoading = false,
  } = props;

  const canRespondToRequest =
    collaborationStatus === CollaborationStatus.REQUESTED && !isRequester;
  const isDeclined = collaborationStatus === CollaborationStatus.RECEIVER_DECLINED;

  const handleView = () => {
    if (isDeclined && onOpenWorkspace) {
      onOpenWorkspace(matchId);
      return;
    }
    onViewProfile?.(companyId);
  };

  return (
    <>
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            radius="md"
            aria-label="Row actions"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <IconDotsVertical size={18} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          <Menu.Item
            leftSection={<IconEye size={16} />}
            onClick={(e) => {
              e.stopPropagation();
              handleView();
            }}
          >
            View
          </Menu.Item>
          {canRespondToRequest ? (
            <>
              <Menu.Item
                leftSection={<IconCheck size={16} />}
                onClick={(e) => {
                  e.stopPropagation();
                  openAcceptModal();
                }}
              >
                Accept
              </Menu.Item>
              <Menu.Item
                leftSection={<IconX size={16} color="var(--mantine-color-red-6)" />}
                color="red"
                onClick={(e) => {
                  e.stopPropagation();
                  openReject();
                }}
              >
                Decline
              </Menu.Item>
            </>
          ) : null}
        </Menu.Dropdown>
      </Menu>

      <AcceptInterestModal
        opened={acceptModalOpened}
        onClose={closeAcceptModal}
        onConfirm={() => {
          closeAcceptModal();
          onAccept?.(matchId);
        }}
        isLoading={isLoading}
      />

      <InterestActionConfirmModal
        opened={rejectOpened}
        onClose={closeReject}
        title="Reject collaboration request?"
        message="Do you want to reject this collaboration request?"
        confirmLabel="Yes, reject"
        confirmColor="red"
        isLoading={isLoading}
        onConfirm={() => {
          closeReject();
          onDecline?.(matchId);
        }}
      />
    </>
  );
}
